'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { offlineDB, type OfflineVerification, type OfflineBoothInfo } from '@/lib/offline-db';
import { useOfflineSync } from '@/hooks/use-offline-sync';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  FileBarChart,
  Printer,
  Loader2,
  ArrowLeft,
  Users,
  Calendar,
  AlertCircle,
  UserCheck,
  User,
  UserPlus,
  BarChart3,
  Shield,
  Building2,
} from 'lucide-react';

interface Report15AStats {
  totalVoters: number;
  totalVerifications: number;
  maleVerifications: number;
  femaleVerifications: number;
  otherVerifications: number;
  verificationPercentage: string;
  syncedVerifications: number;
  pendingVerifications: number;
  electionStaffCount: number;
}

interface CandidateRow {
  name: string;
  address: string;
  party: string;
}

export function Report15A() {
  const { token, user } = useAuthStore();
  const { setCurrentView, isOnline } = useAppStore();
  const { pendingCount } = useOfflineSync();

  const [boothInfo, setBoothInfo] = useState<OfflineBoothInfo | null>(null);
  const [stats, setStats] = useState<Report15AStats>({
    totalVoters: 0,
    totalVerifications: 0,
    maleVerifications: 0,
    femaleVerifications: 0,
    otherVerifications: 0,
    verificationPercentage: '0',
    syncedVerifications: 0,
    pendingVerifications: 0,
    electionStaffCount: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [reportDate, setReportDate] = useState('');
  const [electionDate, setElectionDate] = useState('');
  const [candidates, setCandidates] = useState<CandidateRow[]>([
    { name: '', address: '', party: '' },
    { name: '', address: '', party: '' },
    { name: '', address: '', party: '' },
  ]);

  const boothId = user?.boothId || '';

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await offlineDB.init();

        // Load booth info from IndexedDB
        const info = await offlineDB.getBoothInfo(boothId);
        if (info) {
          setBoothInfo(info);
        }

        // Load verifications from IndexedDB for stats
        const verifications: OfflineVerification[] = await offlineDB.getVerifications(boothId);
        const totalVoters = info?.totalVoters || 0;
        const totalVerifications = verifications.length;
        const maleVerifications = verifications.filter((v) => v.gender === 'पुरुष').length;
        const femaleVerifications = verifications.filter((v) => v.gender === 'महिला').length;
        const otherVerifications = totalVerifications - maleVerifications - femaleVerifications;
        const syncedVerifications = verifications.filter((v) => v.synced === 1).length;
        const pendingVerifications = verifications.filter((v) => v.synced === 0).length;
        const verificationPercentage = totalVoters > 0
          ? ((totalVerifications / totalVoters) * 100).toFixed(2)
          : '0';

        setStats({
          totalVoters,
          totalVerifications,
          maleVerifications,
          femaleVerifications,
          otherVerifications,
          verificationPercentage,
          syncedVerifications,
          pendingVerifications,
          electionStaffCount: 0,
        });

        // If online, try to fetch from API for complete data
        if (isOnline && token) {
          try {
            const res = await fetch(`/api/reports?boothId=${boothId}&type=15a`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              const data = await res.json();
              if (data.success && data.booth) {
                setBoothInfo((prev) => prev || {
                  pollingBoothId: data.booth.pollingBoothId,
                  name: data.booth.name,
                  address: data.booth.address || '',
                  district: data.booth.district || '',
                  constituency: data.booth.constituency || '',
                  constituencyNo: data.booth.constituencyNo || '',
                  wardNo: data.booth.wardNo || '',
                  totalVoters: data.booth.totalVoters || 0,
                  downloadedAt: new Date().toISOString(),
                });

                if (data.stats) {
                  setStats((prev) => ({
                    ...prev,
                    totalVoters: data.stats.totalVoters || prev.totalVoters,
                    totalVerifications: data.stats.totalVerifications || prev.totalVerifications,
                    maleVerifications: data.stats.maleVerifications || prev.maleVerifications,
                    femaleVerifications: data.stats.femaleVerifications || prev.femaleVerifications,
                    otherVerifications: data.stats.otherVerifications || prev.otherVerifications,
                    verificationPercentage: data.stats.verificationPercentage || prev.verificationPercentage,
                    syncedVerifications: data.stats.syncedVerifications || prev.syncedVerifications,
                    pendingVerifications: data.stats.pendingVerifications || prev.pendingVerifications,
                  }));
                }
              }
            }
          } catch {
            // API fetch failed, use offline data
          }
        }

        // Set dates
        const now = new Date();
        setReportDate(now.toLocaleDateString('hi-IN', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        }));
        setElectionDate(now.toLocaleDateString('hi-IN', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        }));
      } catch (error) {
        console.error('Failed to load report data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (boothId) {
      loadData();
    }
  }, [boothId, isOnline, token]);

  const downloadReport = useCallback(() => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const constituencyNo = boothInfo?.constituencyNo || '___';
    const constituency = boothInfo?.constituency || '___';
    const district = boothInfo?.district || '___';
    const boothName = boothInfo?.name || '___';

    // Build candidate rows
    const candidateRows = candidates
      .map(
        (c, i) => `
        <tr>
          <td style="border:1px solid #000;padding:6px 8px;text-align:center;">${i + 1}</td>
          <td style="border:1px solid #000;padding:6px 8px;text-align:left;">${c.name || '—'}</td>
          <td style="border:1px solid #000;padding:6px 8px;text-align:left;">${c.address || '—'}</td>
          <td style="border:1px solid #000;padding:6px 8px;text-align:left;">${c.party || '—'}</td>
        </tr>`
      )
      .join('');

    printWindow.document.write(`<!DOCTYPE html>
<html lang="hi">
<head>
  <meta charset="UTF-8" />
  <title>फॉर्म - 15 अ | Form 15-A</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;500;600;700&display=swap" rel="stylesheet" />
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Noto Sans Devanagari', Arial, sans-serif;
      color: #000;
      background: #fff;
      font-size: 13px;
      line-height: 1.5;
      max-width: 780px;
      margin: 0 auto;
      padding: 30px 20px;
    }

    /* ── Header ── */
    .form-header {
      text-align: center;
      border-bottom: 3px double #000;
      padding-bottom: 14px;
      margin-bottom: 20px;
    }
    .form-header .title {
      font-size: 22px;
      font-weight: 700;
      margin-bottom: 2px;
    }
    .form-header .legal-ref {
      font-size: 12px;
      margin-bottom: 6px;
    }
    .form-header .subtitle {
      font-size: 15px;
      font-weight: 600;
      margin-bottom: 10px;
    }
    .booth-info {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
      gap: 6px 28px;
      font-size: 13px;
    }
    .booth-info span { font-weight: 500; }

    /* ── Sections ── */
    .section {
      margin-bottom: 18px;
    }
    .section-heading {
      font-size: 14px;
      font-weight: 600;
      border-bottom: 1px solid #000;
      padding-bottom: 3px;
      margin-bottom: 10px;
    }

    /* ── Tables ── */
    table.data-table {
      width: 100%;
      border-collapse: collapse;
    }
    table.data-table th,
    table.data-table td {
      border: 1px solid #000;
      padding: 6px 8px;
      font-size: 12px;
      text-align: center;
    }
    table.data-table th {
      font-weight: 600;
      background: #f0f0f0;
    }

    /* ── Stats two-column table ── */
    table.stats-table {
      width: 100%;
      border-collapse: collapse;
    }
    table.stats-table td {
      border: 1px solid #000;
      padding: 6px 10px;
      font-size: 13px;
    }
    table.stats-table td.label-cell {
      font-weight: 500;
      text-align: left;
      width: 70%;
    }
    table.stats-table td.value-cell {
      font-weight: 700;
      text-align: center;
      width: 30%;
    }

    /* ── Election details row ── */
    .detail-row {
      display: flex;
      justify-content: space-between;
      padding: 6px 10px;
      border: 1px solid #000;
      font-size: 13px;
      margin-bottom: -1px;
    }
    .detail-row .label { font-weight: 500; }
    .detail-row .value { font-weight: 700; }

    /* ── Footer / Signature ── */
    .signature-section {
      margin-top: 50px;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
    }
    .sig-block {
      text-align: center;
    }
    .sig-block .line {
      border-top: 1px solid #000;
      width: 200px;
      padding-top: 4px;
      margin-top: 40px;
      font-size: 12px;
      font-weight: 500;
    }
    .sig-block .line.narrow { width: 140px; }

    /* ── Print ── */
    @media print {
      body { padding: 10px 5px; max-width: none; }
      @page { size: A4; margin: 12mm; }
    }
  </style>
</head>
<body>

  <!-- ═══════ HEADER ═══════ -->
  <div class="form-header">
    <div class="title">फॉर्म - 15 अ</div>
    <div class="legal-ref">म.प्र. निर्वाचन अधिनियम, 1995 की धारा 72 के तहत</div>
    <div class="subtitle">चुनाव सारांश रिपोर्ट</div>
    <div class="booth-info">
      <div>निर्वाचन क्षेत्र न.: <span>${constituencyNo}</span></div>
      <div>निर्वाचन क्षेत्र: <span>${constituency}</span></div>
      <div>जिला: <span>${district}</span></div>
    </div>
    <div class="booth-info" style="margin-top:4px;">
      <div>मतदान कक्ष: <span>${boothName}</span></div>
    </div>
  </div>

  <!-- ═══════ SECTION 1: Candidate Details ═══════ -->
  <div class="section">
    <div class="section-heading">1. उम्मीदवार विवरण</div>
    <table class="data-table">
      <thead>
        <tr>
          <th style="width:40px;">क्र.</th>
          <th>उम्मीदवार का नाम</th>
          <th>पता</th>
          <th>राजनैतिक दल / स्वतंत्र</th>
        </tr>
      </thead>
      <tbody>
        ${candidateRows}
      </tbody>
    </table>
  </div>

  <!-- ═══════ SECTION 2: Election Details ═══════ -->
  <div class="section">
    <div class="section-heading">2. निर्वाचन विवरण</div>
    <div class="detail-row">
      <span class="label">मतदान का दिनांक:</span>
      <span class="value">${electionDate || '___'}</span>
    </div>
    <div class="detail-row">
      <span class="label">मतगणना का दिनांक:</span>
      <span class="value">___</span>
    </div>
  </div>

  <!-- ═══════ SECTION 3: Key Statistics ═══════ -->
  <div class="section">
    <div class="section-heading">3. मतदान सांख्यिकी</div>
    <table class="stats-table">
      <tr>
        <td class="label-cell">पंजीकृत मतदाताओं की कुल संख्या</td>
        <td class="value-cell">${stats.totalVoters}</td>
      </tr>
      <tr>
        <td class="label-cell">सत्यापित मतदाताओं की कुल संख्या</td>
        <td class="value-cell">${stats.totalVerifications}</td>
      </tr>
      <tr>
        <td class="label-cell">सकल मतदान प्रतिशत</td>
        <td class="value-cell">${stats.verificationPercentage}%</td>
      </tr>
      <tr>
        <td class="label-cell">पुरुष मतदाता (सत्यापित)</td>
        <td class="value-cell">${stats.maleVerifications}</td>
      </tr>
      <tr>
        <td class="label-cell">महिला मतदाता (सत्यापित)</td>
        <td class="value-cell">${stats.femaleVerifications}</td>
      </tr>
      <tr>
        <td class="label-cell">अन्य मतदाता (सत्यापित)</td>
        <td class="value-cell">${stats.otherVerifications}</td>
      </tr>
    </table>
  </div>

  <!-- ═══════ FOOTER ═══════ -->
  <div class="signature-section">
    <div class="sig-block">
      <div class="line narrow">दिनांक</div>
    </div>
    <div class="sig-block">
      <div class="line">मतदान अधिकारी का हस्ताक्षर</div>
    </div>
  </div>

</body>
</html>`);

    printWindow.document.close();

    // 500ms delay to allow font loading before triggering print
    setTimeout(() => {
      printWindow.print();
    }, 500);
  }, [boothInfo, stats, candidates, electionDate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-3">
          <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto" />
          <p className="text-emerald-800 font-medium">रिपोर्ट लोड हो रही है...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-4 space-y-4">
      {/* Header Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileBarChart className="w-6 h-6 text-emerald-700" />
          <div>
            <h2 className="text-lg font-bold text-emerald-900">फॉर्म - 15 अ</h2>
            <p className="text-xs text-gray-500">चुनाव सारांश रिपोर्ट | Election Summary Report</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">
            <BarChart3 className="w-3 h-3 mr-1" />
            {stats.verificationPercentage}% मतदान
          </Badge>
          {pendingCount > 0 && (
            <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">
              {pendingCount} लंबित सिंक
            </Badge>
          )}
          <Button
            onClick={downloadReport}
            className="bg-emerald-600 hover:bg-emerald-700 text-white min-h-[44px]"
          >
            <Printer className="w-4 h-4 mr-2" />
            प्रिंट / डाउनलोड
          </Button>
        </div>
      </div>

      {/* Report Content - Printable */}
      <Card className="border-emerald-200 shadow-md" id="report-15a-content">
        <CardContent className="p-6">
          {/* Header Section */}
          <div className="text-center mb-6 pb-4 border-b-4 border-double border-emerald-800">
            <h1 className="text-2xl font-bold text-emerald-900 mb-1">फॉर्म - 15 अ</h1>
            <p className="text-xs text-gray-600 mb-3">
              म.प्र. निर्वाचन अधिनियम, 1995 की धारा 72 के तहत
            </p>
            <p className="text-sm font-semibold text-emerald-800 mb-2">चुनाव सारांश रिपोर्ट</p>

            <div className="flex items-center justify-center gap-6 text-sm flex-wrap">
              <span>
                <span className="text-gray-500">निर्वाचन क्षेत्र न.:</span>{' '}
                <span className="font-semibold">{boothInfo?.constituencyNo || '___'}</span>
              </span>
              <span>
                <span className="text-gray-500">निर्वाचन क्षेत्र:</span>{' '}
                <span className="font-semibold">{boothInfo?.constituency || '___'}</span>
              </span>
              <span>
                <span className="text-gray-500">जिला:</span>{' '}
                <span className="font-semibold">{boothInfo?.district || '___'}</span>
              </span>
            </div>

            <div className="flex items-center justify-center gap-4 mt-2 text-sm">
              <span>
                <span className="text-gray-500">मतदान कक्ष:</span>{' '}
                <span className="font-semibold">{boothInfo?.name || '___'}</span>
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3 text-gray-400" />
                <span className="text-gray-500">दिनांक:</span>{' '}
                <span className="font-semibold">{reportDate}</span>
              </span>
            </div>
          </div>

          {/* Section 1: Candidate Details */}
          <div className="mb-6">
            <h3 className="text-base font-semibold text-emerald-800 border-b border-emerald-600 pb-1 mb-3">
              1. उम्मीदवार विवरण (Candidate Details)
            </h3>
            <div className="overflow-x-auto">
              <Table className="border border-emerald-800">
                <TableHeader>
                  <TableRow className="bg-emerald-100 hover:bg-emerald-100">
                    <TableHead className="border border-emerald-800 text-center text-emerald-900 font-bold w-[50px]">
                      क्र.
                    </TableHead>
                    <TableHead className="border border-emerald-800 text-center text-emerald-900 font-bold">
                      उम्मीदवार का नाम
                    </TableHead>
                    <TableHead className="border border-emerald-800 text-center text-emerald-900 font-bold">
                      पता
                    </TableHead>
                    <TableHead className="border border-emerald-800 text-center text-emerald-900 font-bold">
                      राजनैतिक दल / स्वतंत्र
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {candidates.map((_, index) => (
                    <TableRow key={index} className="hover:bg-emerald-50/50">
                      <TableCell className="border border-emerald-800/30 text-center font-medium">
                        {index + 1}
                      </TableCell>
                      <TableCell className="border border-emerald-800/30 text-center text-gray-400">
                        —
                      </TableCell>
                      <TableCell className="border border-emerald-800/30 text-center text-gray-400">
                        —
                      </TableCell>
                      <TableCell className="border border-emerald-800/30 text-center text-gray-400">
                        —
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Section 2: Election Details */}
          <div className="mb-6">
            <h3 className="text-base font-semibold text-emerald-800 border-b border-emerald-600 pb-1 mb-3">
              2. निर्वाचन विवरण (Election Details)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <span className="text-sm text-gray-600">मतदान का दिनांक:</span>
                <span className="text-sm font-semibold text-emerald-800">{electionDate || '___'}</span>
              </div>
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <span className="text-sm text-gray-600">मतगणना का दिनांक:</span>
                <span className="text-sm font-semibold text-emerald-800">___</span>
              </div>
            </div>
          </div>

          {/* Section 3: Key Statistics - MOST IMPORTANT */}
          <div className="mb-6">
            <h3 className="text-base font-semibold text-emerald-800 border-b border-emerald-600 pb-1 mb-3">
              3. मतदान सांख्यिकी (Voting Statistics)
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Total Registered Voters */}
              <StatRow
                icon={<Users className="w-4 h-4 text-emerald-600" />}
                label="पंजीकृत मतदाताओं की कुल संख्या"
                labelEn="Total Registered Voters"
                value={stats.totalVoters.toString()}
                highlight
              />

              {/* Total Election Staff */}
              <StatRow
                icon={<Shield className="w-4 h-4 text-gray-500" />}
                label="चुनाव कर्मचारियों की कुल संख्या"
                labelEn="Total Election Staff"
                value={stats.electionStaffCount.toString()}
              />

              {/* Total Verified Voters - KEY STAT */}
              <StatRow
                icon={<UserCheck className="w-4 h-4 text-emerald-600" />}
                label="सत्यापित मतदाताओं की कुल संख्या"
                labelEn="Total Verified Voters"
                value={stats.totalVerifications.toString()}
                highlight
              />

              {/* Voting Percentage */}
              <StatRow
                icon={<BarChart3 className="w-4 h-4 text-emerald-600" />}
                label="सकल मतदान प्रतिशत"
                labelEn="Total Voting Percentage"
                value={`${stats.verificationPercentage}%`}
                highlight
              />

              {/* Male Voters */}
              <StatRow
                icon={<User className="w-4 h-4 text-blue-600" />}
                label="पुरुष मतदाता (सत्यापित)"
                labelEn="Male Voters Verified"
                value={stats.maleVerifications.toString()}
              />

              {/* Female Voters */}
              <StatRow
                icon={<UserPlus className="w-4 h-4 text-pink-600" />}
                label="महिला मतदाता (सत्यापित)"
                labelEn="Female Voters Verified"
                value={stats.femaleVerifications.toString()}
              />

              {/* Other Voters */}
              <StatRow
                icon={<User className="w-4 h-4 text-gray-600" />}
                label="अन्य मतदाता (सत्यापित)"
                labelEn="Other Voters Verified"
                value={stats.otherVerifications.toString()}
              />

              {/* Synced vs Pending */}
              <StatRow
                icon={<Building2 className="w-4 h-4 text-emerald-500" />}
                label="सिंक स्थिति (सिंक/लंबित)"
                labelEn="Sync Status (Synced/Pending)"
                value={`${stats.syncedVerifications} / ${stats.pendingVerifications}`}
              />
            </div>
          </div>

          {/* Visual Progress */}
          <div className="mb-6">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-emerald-800">मतदान प्रगति</span>
                <span className="text-sm font-bold text-emerald-700">{stats.verificationPercentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-4 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(parseFloat(stats.verificationPercentage), 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0%</span>
                <span>सत्यापित: {stats.totalVerifications} / {stats.totalVoters}</span>
                <span>100%</span>
              </div>
            </div>
          </div>

          <Separator className="my-6" />

          {/* Footer / Signature Section */}
          <div className="mt-6 pt-4 border-t border-gray-300">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-gray-500 mb-1">
                  रिपोर्ट दिनांक: <span className="font-medium text-gray-700">{reportDate}</span>
                </p>
                <p className="text-xs text-gray-500">
                  मतदान कक्ष: <span className="font-medium text-gray-700">{boothInfo?.name || '___'}</span>
                </p>
              </div>
              <div className="text-center">
                <div className="w-48 border-t border-gray-800 pt-2 mt-8">
                  <p className="text-sm font-medium">मतदान अधिकारी का हस्ताक्षर</p>
                  <p className="text-xs text-gray-500 mt-1">Signature of Polling Officer</p>
                </div>
              </div>
              <div className="text-center">
                <div className="w-32 border-t border-gray-800 pt-2 mt-8">
                  <p className="text-sm font-medium">दिनांक</p>
                  <p className="text-xs text-gray-500 mt-1">Date</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Back Button */}
      <div className="flex justify-center pt-2 pb-4">
        <Button
          variant="outline"
          onClick={() => setCurrentView('officer-dashboard')}
          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          वापस जाएं
        </Button>
      </div>
    </div>
  );
}

// Stat row component for the 15-A report
function StatRow({
  icon,
  label,
  labelEn,
  value,
  highlight = false,
}: {
  icon: React.ReactNode;
  label: string;
  labelEn: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg border ${
        highlight
          ? 'bg-emerald-50 border-emerald-300'
          : 'bg-white border-gray-200'
      }`}
    >
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded ${highlight ? 'bg-emerald-100' : 'bg-gray-100'}`}>
          {icon}
        </div>
        <div>
          <p className={`text-sm font-medium ${highlight ? 'text-emerald-900' : 'text-gray-700'}`}>
            {label}
          </p>
          <p className="text-xs text-gray-400">{labelEn}</p>
        </div>
      </div>
      <span className={`text-lg font-bold ${highlight ? 'text-emerald-700' : 'text-gray-800'}`}>
        {value}
      </span>
    </div>
  );
}
